import React, { useEffect, useState } from 'react';
import styles from './related_records.mod.less';
import renderIf from 'render-if';
import terminology from 'javascripts/terminology';
import * as Sentry from '@sentry/browser';
import ReactiveContainer from 'components/common/ReactiveContainer';

export const relatedRecordTypes = {
  StrategicImperative: ['Initiative', 'Release', 'Epic', 'Feature'],
  Initiative: ['Release', 'Epic', 'Feature'],
  Release: ['Epic', 'Feature'],
  ReleasePhase: ['Feature'],
  Epic: ['Feature'],
  Feature: [],
  Requirement: [],
};

/**
 * @param {{ bookmarkId: string, recordId: string, recordType: string, recordEpicsEnabled: boolean }} props
 */

/**
 * @typedef RelatedRecordsProps
 * @prop {*=} bookmarkId
 * @prop {*=} recordId
 * @prop {*=} recordType
 * @prop {*=} recordEpicsEnabled
 */
/**
 * @type {React.FC<RelatedRecordsProps>}
 */
function RelatedRecords({
  bookmarkId,
  recordId,
  recordType,
  recordEpicsEnabled,
}) {
  useEffect(() => {
    Sentry.addBreadcrumb({
      message: `Related records view opened for ${recordType}: ${recordId}`,
    });

    // Log error for invalid record type
    if (recordType && !(recordType in relatedRecordTypes)) {
      const error = new Error(
        `Invalid recordType for RelatedRecords: ${recordType}`
      );
      Sentry.captureException(error);
    }
  }, [recordId, recordType]);

  return (
    <div className={styles.popover}>
      <div className={styles.popoverScrollView}>
        <div style={{ display: 'flex', gap: '1em', flexDirection: 'column' }}>
          <h4>Linked records</h4>

          <ReactiveContainer
            reactiveUrl={`/bookmarks/custom_pivots/${bookmarkId}/record_links?record_type=${recordType}&record_id=${recordId}`}
          />

          {relatedRecordTypes[recordType]
            ?.filter(
              relatedRecordType =>
                relatedRecordType !== 'Epic' || recordEpicsEnabled
            )
            .map(relatedRecordType => (
              <Section
                key={relatedRecordType}
                forRecordType={recordType}
                forRecordId={recordId}
                relatedRecordType={relatedRecordType}
                bookmarkId={bookmarkId}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
export default RelatedRecords;

/**
 * @param {{
 *   forRecordType: string;
 *   forRecordId:string;
 *   bookmarkId: string;
 *   relatedRecordType: string;
 * }} props
 */

/**
 * @typedef SectionProps
 * @prop {*=} bookmarkId
 * @prop {*=} forRecordType
 * @prop {*=} forRecordId
 * @prop {*=} relatedRecordType
 */
/**
 * @type {React.FC<SectionProps>}
 */
function Section({
  bookmarkId,
  forRecordType,
  forRecordId,
  relatedRecordType,
}) {
  const [isOpen, setIsOpen] = useState(false);

  function toggle() {
    setIsOpen(!isOpen);
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <h4>{terminology(relatedRecordType, { isPlural: true })}</h4>
        <button onClick={toggle} className='btn btn-link'>
          {isOpen ? 'Hide' : 'Show'}{' '}
          {terminology(relatedRecordType, { isPlural: true }).toLowerCase()}
        </button>
      </div>

      {renderIf(isOpen)(
        <ReactiveContainer
          reactiveUrl={`/bookmarks/custom_pivots/${bookmarkId}/related_records?record_type=${forRecordType}&record_id=${forRecordId}&related_record_type=${relatedRecordType}`}
        />
      )}
    </>
  );
}
